import { Rocket, ShieldCheck } from 'lucide-react';
import { getAliceWorkflow } from '@command-center/core-platform';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { useUiStore } from '@/stores/ui';
import { demoSwitchboardAllows } from '@/platform/demo-mode';

/**
 * Command Center Switchboard — a launch grid for Alice's most common workflows.
 * Each card opens Alice and runs its slash command. Display name / risk / status
 * are pulled from the shared ALICE_WORKFLOWS catalog (by key) so the page can't
 * drift from what the workflows actually are. Draft-only — nothing here takes an
 * external action; Alice produces plans/checklists/drafts for review.
 */

interface SwitchboardCard {
  seed: string;
  workflowKey: string;
  /** Fallback label if the workflow isn't found in the shared catalog. */
  fallbackTitle: string;
  blurb: string;
}

const CARDS: SwitchboardCard[] = [
  { seed: '/add-lead', workflowKey: 'lead.add_lead', fallbackTitle: 'Add Lead', blurb: 'Draft a clean, deduplicated lead from rough notes.' },
  { seed: '/website-build-intake', workflowKey: 'website.build_intake', fallbackTitle: 'Website Build Intake', blurb: 'Assemble a build packet from the org + website records.' },
  { seed: '/client-summary', workflowKey: 'client.project_summary', fallbackTitle: 'Client Project Summary', blurb: 'At-a-glance status of a client and its active work.' },
  { seed: '/prep-call', workflowKey: 'client.prep_call', fallbackTitle: 'Prep Client Call', blurb: 'Briefing + agenda before a client call or meeting.' },
  { seed: '/proposal-check', workflowKey: 'proposal.readiness_check', fallbackTitle: 'Proposal Readiness Check', blurb: 'Flag what is missing before a proposal goes out.' },
  { seed: '/archive-website', workflowKey: 'website.archive_prep', fallbackTitle: 'Archive Website / Client', blurb: 'Safe pre-offboarding checklist + risk list.' },
  { seed: '/deals-verify-prep', workflowKey: 'deals.verify_prep', fallbackTitle: 'Daily Deals Verification Prep', blurb: 'Verification checklist before a deal is confirmed.' },
  { seed: '/callback-prep', workflowKey: 'lead.callback_prep', fallbackTitle: 'Curiosity Callback Prep', blurb: 'Warm, informed prep for an inbound callback.' },
];

export function SwitchboardView() {
  const openCopilotPanel = useUiStore((state) => state.openCopilotPanel);
  const seedCopilotPanel = useUiStore((state) => state.seedCopilotPanel);

  const launch = (seed: string) => {
    openCopilotPanel();
    seedCopilotPanel(seed);
  };

  return (
    <div className="mx-auto flex w-full max-w-[1100px] flex-col gap-4">
      <header className="space-y-1">
        <h1 className="flex items-center gap-2 text-[22px] font-semibold leading-tight">
          <Rocket className="h-5 w-5 text-primary" aria-hidden />
          Switchboard
        </h1>
        <p className="text-[13px] text-muted-foreground">
          Launch a common Alice workflow. Each opens Alice and runs the command for you.
        </p>
      </header>

      <Card className="flex items-start gap-2 border-primary/20 bg-primary/5 px-3.5 py-2.5 text-[12px] text-muted-foreground">
        <ShieldCheck className="mt-0.5 h-4 w-4 flex-shrink-0 text-primary" aria-hidden />
        <span>
          Draft-only — these workflows produce plans, checklists, and drafts for you to review.
          Nothing here sends a message, calls anyone, or changes a record.
        </span>
      </Card>

      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
        {CARDS.filter((card) => demoSwitchboardAllows(card.workflowKey)).map((card) => {
          const wf = getAliceWorkflow(card.workflowKey);
          const title = wf?.displayName ?? card.fallbackTitle;
          const description = wf?.description ?? card.blurb;
          const runnable = !wf || (wf.enabled && wf.status !== 'paused' && wf.status !== 'retired');
          return (
            <Card key={card.workflowKey} className="flex flex-col gap-3 p-4">
              <div className="flex flex-wrap items-center gap-1.5">
                <h2 className="text-[14px] font-semibold leading-tight text-foreground">{title}</h2>
              </div>
              <p className="min-h-[2.5rem] flex-1 text-[12px] leading-snug text-muted-foreground">
                {description}
              </p>
              <div className="flex flex-wrap items-center gap-1.5">
                {wf && (
                  <Badge variant="muted" className="h-5 px-1.5 text-[10px]">
                    Risk {wf.riskLevel}
                  </Badge>
                )}
                {wf && (
                  <Badge
                    variant={wf.status === 'active' ? 'success' : 'outline'}
                    className="h-5 px-1.5 text-[10px]"
                  >
                    {wf.status}
                  </Badge>
                )}
                <code className="rounded bg-muted px-1.5 py-0.5 font-mono text-[10px] text-muted-foreground">
                  {card.seed}
                </code>
              </div>
              <Button
                type="button"
                size="sm"
                className="mt-1 gap-1.5"
                onClick={() => launch(card.seed)}
                disabled={!runnable}
              >
                <Rocket className="h-3.5 w-3.5" aria-hidden />
                Launch
              </Button>
            </Card>
          );
        })}
      </div>
    </div>
  );
}
